import { useState } from "react";
import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    serverTimestamp,
} from "firebase/firestore";

import { db } from "../../firebase";
import { CLUB_ID, THEME_COLOR } from "../../config/club";
import Swal from "sweetalert2";

const TEAM_COLORS = [
    "#dc3545",
    "#0d6efd",
    "#198754",
    "#fd7e14",
    "#6f42c1",
    "#20c997",
];

export default function TeamsSection({
    sessionId,
    players,
    teams,
    teamPlayers,
    setTeams,
    setTeamPlayers
}) {
    const [teamName, setTeamName] = useState("");
    const [selectedColor, setSelectedColor] = useState(
        TEAM_COLORS[0]
    );

    const createTeam = async () => {
        if (!teamName.trim()) return;

        await addDoc(
            collection(
                db,
                "clubs",
                CLUB_ID,
                "sessions",
                sessionId,
                "teams"
            ),
            {
                name: teamName,
                color: selectedColor,
                createdAt: serverTimestamp(),
            }
        );

        setTeamName("");
    };

    const deleteTeam = async (teamId, teamName) => {
        const membersCount = teamPlayers?.[teamId]?.length || 0;

        if (membersCount > 0) {
            Swal.fire({
                icon: "warning",
                title: "Team has players",
                text: `Remove all ${membersCount} player(s) before deleting ${teamName}.`,
                confirmButtonColor: THEME_COLOR,
            });

            return;
        }

        const result = await Swal.fire({
            title: `Delete ${teamName}?`,
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Delete Team",
        });

        if (!result.isConfirmed) return;

        await deleteDoc(
            doc(
                db,
                "clubs",
                CLUB_ID,
                "sessions",
                sessionId,
                "teams",
                teamId
            )
        );
    };

    const removePlayerFromTeam = async (
        teamId,
        playerId,
        playerName
    ) => {
        const result = await Swal.fire({
            title: `Remove ${playerName}?`,
            text: "Player will be removed from this team.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: THEME_COLOR,
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Remove Player",
        });

        if (!result.isConfirmed) return;

        await deleteDoc(
            doc(
                db,
                "clubs",
                CLUB_ID,
                "sessions",
                sessionId,
                "teams",
                teamId,
                "players",
                playerId
            )
        );
    };

    return (
        <div className="mt-4 mb-4">
            {/* HEADER */}
            <div
                className="card border-0 shadow-sm rounded-4 mb-4 overflow-hidden"
                style={{
                    background: "var(--card-bg)",
                    color: "var(--text-color)",
                }}
            >
                <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                        <div>
                            <div
                                className="fw-black d-flex align-items-center gap-2"
                                style={{
                                    color: THEME_COLOR,
                                    fontSize: "1rem",
                                }}
                            >
                                <i className="bi bi-shield-fill"></i>
                                Tournament Teams
                            </div>

                            <small
                                style={{
                                    color: "var(--text-muted)",
                                }}
                            >
                                Create and manage session teams
                            </small>
                        </div>

                        <div className="small fw-bold text-muted">
                            {teams.length} Teams
                        </div>
                    </div>
                </div>
            </div>

            {/* CREATE TEAM */}
            <div
                className="card border-0 shadow-sm rounded-4 mb-4"
                style={{
                    background: "var(--card-bg)",
                    color: "var(--text-color)",
                }}
            >
                <div className="card-body p-3">
                    <div className="row g-3 align-items-center">
                        <div className="col-md-5">
                            <input
                                type="text"
                                className="form-control rounded-pill border-0 shadow-none"
                                placeholder="Enter team name..."
                                value={teamName}
                                onChange={(e) =>
                                    setTeamName(e.target.value)
                                }
                                style={{
                                    background:
                                        "var(--surface-bg)",
                                    color: "var(--text-color)",
                                }}
                            />
                        </div>

                        <div className="col-md-4">
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                {TEAM_COLORS.map((color) => (
                                    <button
                                        key={color}
                                        className="border-0 rounded-circle transition-all"
                                        onClick={() =>
                                            setSelectedColor(color)
                                        }
                                        style={{
                                            width: 30,
                                            height: 30,
                                            backgroundColor: color,
                                            outline:
                                                selectedColor === color
                                                    ? `3px solid var(--text-color)`
                                                    : "none",
                                            transform:
                                                selectedColor === color
                                                    ? "scale(1.15)"
                                                    : "scale(1)",
                                        }}
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="col-md-3">
                            <button
                                className="btn w-100 rounded-pill fw-bold text-white"
                                style={{
                                    backgroundColor:
                                        THEME_COLOR,
                                }}
                                onClick={createTeam}
                            >
                                <i className="bi bi-plus-circle me-2"></i>
                                Create Team
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* TEAM GRID */}
            <div className="row g-3">
                {teams.length === 0 ? (
                    <div className="col-12">
                        <div
                            className="card border-0 shadow-sm rounded-4 text-center py-5"
                            style={{
                                background:
                                    "var(--card-bg)",
                                color: "var(--text-color)",
                            }}
                        >
                            <i
                                className="bi bi-shield-x opacity-25"
                                style={{
                                    fontSize: "3rem",
                                }}
                            ></i>

                            <div className="mt-2 text-muted">
                                No teams created yet
                            </div>
                        </div>
                    </div>
                ) : (
                    teams.map((team) => (
                        <div
                            key={team.id}
                            className="col-md-6 col-xl-4"
                        >
                            <div
                                className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden hover-lift"
                                style={{
                                    background:
                                        "var(--card-bg)",
                                    color:
                                        "var(--text-color)",
                                }}
                            >
                                {/* TOP BAR */}
                                <div
                                    style={{
                                        height: 5,
                                        backgroundColor:
                                            team.color,
                                    }}
                                ></div>

                                <div className="card-body p-3">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div className="d-flex align-items-center gap-3">
                                            <div
                                                className="rounded-circle shadow-sm"
                                                style={{
                                                    width: 16,
                                                    height: 16,
                                                    backgroundColor:
                                                        team.color,
                                                }}
                                            />

                                            <div>
                                                <div className="fw-black">
                                                    {team.name}
                                                </div>

                                                <small
                                                    style={{
                                                        color:
                                                            "var(--text-muted)",
                                                    }}
                                                >
                                                    Tournament Team
                                                </small>
                                            </div>
                                        </div>

                                        <button
                                            className="btn btn-sm btn-light border-0 rounded-circle"
                                            style={{
                                                width: 32,
                                                height: 32,
                                            }}
                                            onClick={() =>
                                                deleteTeam(team.id, team.name)
                                            }
                                        >
                                            <i className="bi bi-trash text-danger"></i>
                                        </button>
                                    </div>

                                    <hr />

                                    <div className="mt-3">
                                        <div className="small fw-bold text-muted mb-2">
                                            Team Members
                                        </div>

                                        {teamPlayers?.[team.id]?.length ? (
                                            <div className="d-flex flex-column gap-2">
                                                {teamPlayers[team.id].map((player) => (
                                                    <div
                                                        key={player.id}
                                                        className="d-flex align-items-center justify-content-between rounded-pill px-3 py-2"
                                                        style={{
                                                            background: "var(--surface-bg)"
                                                        }}
                                                    >
                                                        <div className="d-flex align-items-center gap-2">
                                                            <i className="bi bi-person-fill"></i>
                                                            <span>{player.name}</span>
                                                        </div>

                                                        <button
                                                            className="btn btn-sm text-danger border-0"
                                                            onClick={() =>
                                                                removePlayerFromTeam(
                                                                    team.id,
                                                                    player.id,
                                                                    player.name
                                                                )
                                                            }
                                                        >
                                                            <i className="bi bi-x-lg"></i>
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-muted small">
                                                No players yet
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <style>{`
                .hover-lift {
                    transition: all 0.2s ease;
                }

                .hover-lift:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 24px rgba(0,0,0,0.12) !important;
                }

                .transition-all {
                    transition: all 0.2s ease;
                }
            `}</style>
        </div>
    );
}